// ── BCHNostr — sidebar navigation usage tracking ─────────────────
//
// Powers the sidebar's auto-populated "Quick Access" zone. This is a
// deliberately conservative take on a "dynamic sidebar": the canonical,
// fully-ordered nav list NEVER moves (spatial memory is preserved). We only
// surface a small set of shortcuts at the TOP, derived from how often — and
// how recently — the user visits each destination.
//
// Design notes:
//   · Frameworkless + localStorage only, matching the app's other lib/ code.
//   · A recency-weighted score, not a raw count: each visit adds 1, but the
//     accumulated score decays a little on every new visit so a burst of
//     usage months ago doesn't permanently pin an item the user has moved on
//     from. This keeps Quick Access feeling "current" without reordering the
//     main list.
//   · Paths are the same strings used in NAV_ITEMS (including query strings
//     like `/settings?tab=wallet`) so the consumer can map straight back.

const STORAGE_KEY = 'bchnostr:nav-usage';

// How much older scores decay each time ANY item is visited. 0.98 ≈ a visit's
// weight roughly halves after ~35 subsequent visits elsewhere — recent enough
// to feel live, slow enough to be stable.
const DECAY = 0.98;

// Below this score an entry is considered noise and dropped from Quick Access
// (and eventually pruned from storage). Prevents a single stray click from
// lingering forever.
const MIN_SCORE = 0.5;

// Never let the store grow unbounded.
const MAX_ENTRIES = 40;

export interface NavUsageEntry {
  path: string;
  score: number;
  /** Unix ms of the most recent visit — used only for tie-breaking. */
  last: number;
}

type UsageMap = Record<string, NavUsageEntry>;

function read(): UsageMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: UsageMap = {};
    for (const [path, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (!v || typeof v !== 'object') continue;
      const o = v as Record<string, unknown>;
      if (typeof o.score === 'number' && Number.isFinite(o.score)) {
        out[path] = {
          path,
          score: o.score,
          last: typeof o.last === 'number' ? o.last : 0,
        };
      }
    }
    return out;
  } catch {
    return {};
  }
}

function write(map: UsageMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage full/disabled — the feature simply stops learning; it never throws.
  }
}

/**
 * Record a visit to a nav destination. Applies global decay first (so the new
 * visit is measured against slightly-faded history), then bumps this path.
 */
export function recordNavVisit(path: string): void {
  if (!path) return;
  const map = read();

  // Decay everything, dropping entries that fall below the noise floor.
  for (const key of Object.keys(map)) {
    map[key].score *= DECAY;
    if (map[key].score < MIN_SCORE && key !== path) {
      delete map[key];
    }
  }

  const existing = map[path];
  map[path] = {
    path,
    score: (existing?.score ?? 0) + 1,
    last: Date.now(),
  };

  // Cap the store: keep the highest-scoring entries only.
  const entries = Object.values(map);
  if (entries.length > MAX_ENTRIES) {
    entries
      .sort((a, b) => b.score - a.score)
      .slice(MAX_ENTRIES)
      .forEach(e => { delete map[e.path]; });
  }

  write(map);
  notify();
}

/**
 * The user's most-used destinations, highest score first. Only returns entries
 * above the noise floor, so a brand-new user (no history) gets an empty list
 * and the sidebar shows no Quick Access zone at all.
 */
export function getTopNavPaths(limit = 5): string[] {
  return Object.values(read())
    .filter(e => e.score >= MIN_SCORE)
    .sort((a, b) => (b.score - a.score) || (b.last - a.last))
    .slice(0, limit)
    .map(e => e.path);
}

/** Wipe all usage history (exposed for a future "reset" affordance). */
export function clearNavUsage(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  notify();
}

// ── Change notifications ──────────────────────────────────────────
// So the sidebar can re-read Quick Access the moment a visit is recorded,
// without prop-drilling or a full reload.

const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach(fn => { try { fn(); } catch { /* ignore */ } });
}

/** Subscribe to usage changes. Returns an unsubscribe function. */
export function onNavUsageChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
